import { Expenditure } from './hooks/useExpenditures';
import { Income } from './hooks/useIncomes';
import { LifeEvent } from './hooks/useLifeEvents';
import { labelForIncomeType } from './incomeTypes';
import { CashflowFrequency, FrequencyLeftOutReason, leftOutReason } from './utils';

type LeftOutReason = FrequencyLeftOutReason | 'noAmount' | 'noBeforeTaxAmount' | 'noAfterTaxAmount';

export type ReasonLabels = Record<LeftOutReason, string>;

export const REASON_LABELS: ReasonLabels = {
  noAmount: 'No amount recorded',
  noBeforeTaxAmount: 'No before-tax amount',
  noAfterTaxAmount: 'No after-tax amount',
  oneOff: 'One-off',
  variable: 'Variable',
  noFrequency: 'No frequency recorded',
};

// The income total shows a before-tax and an after-tax figure, and an income missing one amount
// still counts in the other
export const INCOME_TOTAL_REASON_LABELS: ReasonLabels = {
  ...REASON_LABELS,
  noBeforeTaxAmount: 'No before-tax amount (in the after-tax total)',
  noAfterTaxAmount: 'No after-tax amount (in the before-tax total)',
};

export const REASON_ORDER = Object.keys(REASON_LABELS) as LeftOutReason[];

export const MISSING_AMOUNT_EXPLANATION = 'Amounts not recorded are not included in the totals';

type RecordedIncome = {
  frequency: CashflowFrequency | null;
  amountPreTax: number | null;
  amountPostTax: number | null;
};

export function incomeLeftOutReason(income: RecordedIncome): LeftOutReason | null {
  const frequencyReason = leftOutReason(income.frequency);
  if (frequencyReason) return frequencyReason;
  if (income.amountPreTax == null && income.amountPostTax == null) return 'noAmount';
  if (income.amountPreTax == null) return 'noBeforeTaxAmount';
  if (income.amountPostTax == null) return 'noAfterTaxAmount';
  return null;
}

export function afterTaxLeftOutReason(income: RecordedIncome): LeftOutReason | null {
  const frequencyReason = leftOutReason(income.frequency);
  if (frequencyReason) return frequencyReason;
  if (income.amountPostTax == null) return 'noAfterTaxAmount';
  return null;
}

export function expenditureLeftOutReason(expenditure: {
  frequency: CashflowFrequency | null;
  amount: number | null;
}): LeftOutReason | null {
  const frequencyReason = leftOutReason(expenditure.frequency);
  if (frequencyReason) return frequencyReason;
  if (expenditure.amount == null) return 'noAmount';
  return null;
}

export function lifeEventLeftOutReason(lifeEvent: { cost: number | null }): LeftOutReason | null {
  return lifeEvent.cost == null ? 'noAmount' : null;
}

const FREQUENCY_EXPLANATIONS: Record<FrequencyLeftOutReason, string> = {
  oneOff: 'One-off amounts are not included in the totals',
  variable:
    'Variable amounts are not included in the totals. Choose a regular frequency to include this amount.',
  noFrequency: 'Amounts with no frequency are not included in the totals',
};

export function frequencyExplanation(frequency: CashflowFrequency | null | undefined): string {
  const reason = leftOutReason(frequency);
  return reason ? FREQUENCY_EXPLANATIONS[reason] : '';
}

export function conversionPreviewNote(
  frequency: CashflowFrequency | null | undefined,
): string | null {
  const reason = leftOutReason(frequency);
  if (reason === 'noFrequency') return 'Choose a frequency to see the amounts';
  return reason ? FREQUENCY_EXPLANATIONS[reason] : null;
}

const NO_DESCRIPTION = 'Item with no description';

function itemName(description: string | null | undefined, fallback?: string | null): string {
  return description?.trim() || fallback || NO_DESCRIPTION;
}

export function incomeName(income: Pick<Income, 'description' | 'incomeType'>): string {
  return itemName(income.description, labelForIncomeType(income.incomeType));
}

export function expenditureName(
  expenditure: Pick<Expenditure, 'description' | 'category'>,
): string {
  return itemName(expenditure.description, expenditure.category);
}

export function lifeEventName(lifeEvent: Pick<LifeEvent, 'description'>): string {
  return itemName(lifeEvent.description);
}

export function withMemberName<T>(
  nameOf: (item: T) => string,
): (item: T & { memberName: string }) => string {
  return item => `${item.memberName}: ${nameOf(item)}`;
}

export function withSectionName<T>(
  section: string,
  nameOf: (item: T) => string,
): (item: T) => string {
  return item => `${section}: ${nameOf(item)}`;
}

export type LeftOutNames = Record<LeftOutReason, string[]>;

function noLeftOutNames(): LeftOutNames {
  const names = {} as LeftOutNames;
  for (const reason of REASON_ORDER) {
    names[reason] = [];
  }
  return names;
}

export function leftOutNames<T>(
  items: T[] | undefined,
  reasonOf: (item: T) => LeftOutReason | null,
  nameOf: (item: T) => string,
): LeftOutNames {
  const names = noLeftOutNames();
  for (const item of items ?? []) {
    const reason = reasonOf(item);
    if (reason) names[reason].push(nameOf(item));
  }
  return names;
}

export function combineLeftOutNames(first: LeftOutNames, second: LeftOutNames): LeftOutNames {
  const names = noLeftOutNames();
  for (const reason of REASON_ORDER) {
    names[reason] = [...first[reason], ...second[reason]];
  }
  return names;
}
